/**
 * Clipboard Plugin (Class-based)
 *
 * Provides copy/paste functionality for tbw-grid.
 * Supports Ctrl+C/Cmd+C for copying and Ctrl+V/Cmd+V for pasting.
 *
 * **With Selection plugin:** Copies selected cells/rows/range
 * **Without Selection plugin:** Copies entire grid
 */

import { resolveCellValue } from '../../core/internal/value-accessor';
import {
  BaseGridPlugin,
  type GridElement,
  type PluginDependency,
  type PluginManifest,
  type PluginQuery,
} from '../../core/plugin/base-plugin';
import type { ColumnConfig } from '../../core/types';
import { readCellDisplayText } from '../shared/cell-text';
import { formatValueAsText, resolveColumns, resolveRows } from '../shared/data-collection';
import { buildClipboardHtml, parseClipboardHtmlPayload } from './clipboard-payload';
import { copyToClipboard } from './copy';
import { parseClipboardText, readFromClipboard } from './paste';
import {
  cloneStructured,
  defaultPasteHandler,
  type ClipboardConfig,
  type CopyDetail,
  type CopyOptions,
  type PasteDetail,
  type PasteTarget,
} from './types';

/**
 * Clipboard Plugin for tbw-grid
 *
 * Brings familiar copy/paste functionality with full keyboard shortcut support
 * (Ctrl/Cmd+C, Ctrl/Cmd+V). Handles single cells, multi-cell selections, and integrates
 * seamlessly with Excel and other spreadsheet applications via tab-delimited output.
 *
 * > **Optional Dependency:** Works best with SelectionPlugin for copying/pasting selected
 * > cells. Without SelectionPlugin, copies the entire grid and pastes at row 0, column 0.
 *
 * ## Installation
 *
 * ```ts
 * import { ClipboardPlugin } from '@toolbox-web/grid/plugins/clipboard';
 * ```
 *
 * ## Keyboard Shortcuts
 *
 * | Shortcut | Action |
 * |----------|--------|
 * | `Ctrl+C` / `Cmd+C` | Copy selected cells |
 * | `Ctrl+V` / `Cmd+V` | Paste into selected cells |
 *
 * ## Paste Behavior by Selection Type
 *
 * | Selection Type | Paste Behavior |
 * |----------------|----------------|
 * | Single cell | Paste expands freely from that cell |
 * | Range selection | Paste is clipped to fit within the selected range |
 * | Row selection | Paste is clipped to the selected rows |
 * | No selection | Paste starts at row 0, column 0 |
 *
 * With `fillSelection: true`, pasting a smaller source into a larger bounded
 * selection tiles the source to fill the whole selection (e.g. one cell fills
 * all selected cells; a 1×2 source fills as `val1, val2, val1, val2`).
 *
 * @example Basic Usage with Excel Compatibility
 * ```ts
 * import '@toolbox-web/grid';
 * import { ClipboardPlugin } from '@toolbox-web/grid/plugins/clipboard';
 * import { SelectionPlugin } from '@toolbox-web/grid/plugins/selection';
 *
 * grid.gridConfig = {
 *   columns: [
 *     { field: 'name', header: 'Name' },
 *     { field: 'email', header: 'Email' },
 *   ],
 *   plugins: [
 *     new SelectionPlugin({ mode: 'range' }),
 *     new ClipboardPlugin({
 *       includeHeaders: true,
 *       delimiter: '\t', // Tab for Excel
 *     }),
 *   ],
 * };
 * ```
 *
 * @example Custom Paste Handler
 * ```ts
 * new ClipboardPlugin({
 *   pasteHandler: (grid, target, data) => {
 *     // Validate or transform data before applying
 *     console.log('Pasting', data.length, 'rows');
 *     return defaultPasteHandler(grid, target, data);
 *   },
 * })
 * ```
 *
 * @see {@link ClipboardConfig} for all configuration options
 * @see SelectionPlugin for enhanced copy/paste with selection
 *
 * @internal Extends BaseGridPlugin
 * @since 0.1.1
 */
export class ClipboardPlugin extends BaseGridPlugin<ClipboardConfig> {
  /**
   * Plugin manifest — declares queries for inter-plugin communication.
   * @internal
   */
  static override readonly manifest: PluginManifest = {
    queries: [{ type: 'clipboard:copy', description: 'Triggers a copy operation and returns the copied text' }],
  };

  /**
   * Plugin dependencies - ClipboardPlugin works best with SelectionPlugin.
   *
   * Without SelectionPlugin: copies entire grid, pastes at row 0 col 0.
   * With SelectionPlugin: copies/pastes based on selection.
   */
  /** @internal */
  static override readonly dependencies: PluginDependency[] = [
    { name: 'selection', required: false, reason: 'Enables copy/paste of selected cells instead of entire grid' },
  ];

  /** @internal */
  readonly name = 'clipboard';

  /** @internal */
  protected override get defaultConfig(): Partial<ClipboardConfig> {
    return {
      includeHeaders: false,
      delimiter: '\t',
      newline: '\n',
      quoteStrings: false,
    };
  }

  // #region Internal State
  /** The last copied text (for reference/debugging) */
  private lastCopied: { text: string; timestamp: number } | null = null;
  /**
   * Raw (structured) values for the last copy, so a same-grid paste round-trips
   * object cells losslessly instead of degrading to the copied display text.
   * `text` is the exact clipboard text; a paste whose text matches restores
   * `rawRows` (data rows only, aligned with the copied columns). Cleared on
   * detach; a non-matching external paste simply ignores it.
   */
  #internalClipboard: { text: string; rawRows: unknown[][]; fields: string[] } | null = null;
  // #endregion

  // #region Lifecycle

  /** @internal */
  override attach(grid: GridElement): void {
    super.attach(grid);

    // Listen for native paste events to get clipboard data synchronously
    // This is more reliable than the async Clipboard API in iframe contexts
    grid.addEventListener('paste', (e) => this.#handleNativePaste(e), {
      signal: this.disconnectSignal,
    });
  }

  /** @internal */
  override detach(): void {
    this.lastCopied = null;
    this.#internalClipboard = null;
  }

  /** @internal */
  override handleQuery(query: PluginQuery): unknown {
    if (query.type === 'clipboard:copy') {
      this.copy();
      return true;
    }
    return undefined;
  }
  // #endregion

  // #region Event Handlers

  /** @internal */
  override onKeyDown(event: KeyboardEvent): boolean {
    const isCopy = (event.ctrlKey || event.metaKey) && event.key === 'c';

    if (isCopy) {
      // Prevent the browser's default copy action so it doesn't overwrite
      // our clipboard write with whatever text is selected in the DOM.
      event.preventDefault();
      this.#handleCopy(event.target as HTMLElement);
      return true;
    }

    // For paste, we do NOT return true - let the native paste event fire
    // so we can access clipboardData synchronously in #handleNativePaste
    return false;
  }
  // #endregion

  // #region Private Methods

  /**
   * Handle copy operation from keyboard shortcut.
   *
   * For keyboard-triggered copies, respects the current selection or
   * falls back to the focused cell from the DOM.
   */
  #handleCopy(target: HTMLElement): void {
    const selection = this.#getSelection();

    // Selection plugin exists but has no ranges → resolve the single active cell.
    // Range mode CLEARS `ranges` on plain keyboard navigation (arrow/Tab without
    // Shift), keeping the focused cell only as the selection `anchor`, and DOM
    // focus is NOT on a cell element after keyboard nav — so prefer the anchor
    // (a visible-column index) and only fall back to the focused DOM cell for
    // the mouse-focus / no-anchor edge. Without the anchor path, keyboard-nav
    // copy silently no-ops.
    if (selection && selection.ranges.length === 0) {
      let row: number | undefined;
      let field: string | undefined;
      if (selection.anchor) {
        row = selection.anchor.row;
        field = this.visibleColumns[selection.anchor.col]?.field;
      } else {
        const focused = this.#getFocusedCellFromDOM(target);
        if (focused) {
          row = focused.row;
          field = this.columns[focused.col]?.field;
        }
      }
      if (row == null || field == null) return;
      this.copy({ rowIndices: [row], columns: [field] });
      return;
    }

    // Delegate to the public copy() method (selection or full grid)
    this.copy();
  }

  /**
   * Handle native paste event (preferred method - works in iframes).
   * Uses synchronous clipboardData from the native paste event.
   *
   * Flow:
   * 1. Parse clipboard text
   * 2. Build target/fields info from selection
   * 3. Emit 'paste' event (for listeners)
   * 4. Call paste handler (if configured) to apply data to grid
   *
   * Selection behavior:
   * - Single cell: paste starts at cell, expands freely
   * - Range/row: paste is clipped to fit within selection bounds
   * - No selection: paste starts at row 0, col 0
   */
  #handleNativePaste(event: ClipboardEvent): void {
    // If the paste target is an editable form control or contenteditable element
    // (e.g. an active cell editor injected by EditingPlugin, a filter input, a
    // header search box), let the browser deliver the paste to that control
    // natively. Without this guard `event.preventDefault()` below would swallow
    // the user's keystroke and the editor input would never receive the text.
    if (this.#isEditablePasteTarget(event.target)) return;

    const text = event.clipboardData?.getData('text/plain');
    if (!text) return;

    // Prevent default to avoid pasting into contenteditable elements
    event.preventDefault();

    const parsed = parseClipboardText(text, this.config);

    // Get target cell from selection via query
    const selection = this.#getSelection();
    const ranges = selection?.ranges ?? [];
    const firstRange = ranges[0];

    // Aggregate the bounding box across ALL selected ranges. A multi-cell
    // selection is frequently expressed as several single-cell ranges (clicking
    // or dragging cell-by-cell), so inspecting `ranges[0]` alone would treat it
    // as a single cell and skip fill/clip — pasting into only the first cell.
    // min/max also normalizes reversed (bottom-up / right-to-left) drags.
    let selMinRow = Infinity;
    let selMinCol = Infinity;
    let selMaxRow = -Infinity;
    let selMaxCol = -Infinity;
    for (const r of ranges) {
      selMinRow = Math.min(selMinRow, r.from.row, r.to.row);
      selMaxRow = Math.max(selMaxRow, r.from.row, r.to.row);
      selMinCol = Math.min(selMinCol, r.from.col, r.to.col);
      selMaxCol = Math.max(selMaxCol, r.from.col, r.to.col);
    }

    // Determine target cell (top-left of the whole selection). Range mode
    // CLEARS `ranges` on plain keyboard navigation (arrow/Tab without Shift),
    // keeping the focused cell only as the selection `anchor`. So when there are
    // no ranges, fall back to the anchor before defaulting to (0,0) — otherwise
    // "copy, arrow to another cell, paste" always lands on cell (0,0) instead of
    // the active cell. Mirrors the focused-cell fallback in #handleCopy.
    const anchor = selection?.anchor ?? null;
    const targetRow = firstRange ? selMinRow : (anchor?.row ?? 0);
    const targetCol = firstRange ? selMinCol : (anchor?.col ?? 0);

    // Multi-cell when the selection spans more than one cell in total, whether
    // it's one large range or many single-cell ranges.
    const axis = selectionAxis(selection);
    const isMultiCell =
      !!firstRange && (axis === 'range' || axis === 'row') && (selMinRow !== selMaxRow || selMinCol !== selMaxCol);

    const bounds = isMultiCell ? { endRow: selMaxRow, endCol: selMaxCol } : null;
    // Selection range indices are visible-column indices (from data-col)
    const maxCol = bounds?.endCol ?? this.visibleColumns.length - 1;

    // Drop a copied header row so a round-trip paste writes only values into
    // cells (never the header label). Strips when this grid is configured to
    // include headers AND every cell of the first parsed row is one of this
    // grid's own column labels — independent of which column is pasted INTO, so
    // cross-column pastes (e.g. "Source terminal" → "Terminal route") strip too.
    // External pastes (first row ≠ labels) are left intact.
    this.#dropCopiedHeaderRow(parsed);

    // Same-grid round-trip: when the pasted text is exactly what THIS grid last
    // copied, restore the raw structured values (lossless object cells) aligned
    // with the header-stripped text rows — this path preserves exact value types
    // (e.g. Date) since it never serializes. For a cross-grid / cross-window
    // paste the in-memory buffer misses, so fall back to the structured payload
    // embedded in the clipboard's text/html. External pastes have neither and
    // use the parsed text.
    let rawRows: unknown[][] | undefined;
    let sourceFields: string[] | undefined;
    if (
      this.#internalClipboard &&
      this.#internalClipboard.text === text &&
      this.#internalClipboard.rawRows.length === parsed.length
    ) {
      rawRows = this.#internalClipboard.rawRows;
      sourceFields = this.#internalClipboard.fields;
    } else {
      const payload = parseClipboardHtmlPayload(event.clipboardData?.getData('text/html'));
      if (payload && payload.rows.length === parsed.length) {
        rawRows = payload.rows;
        sourceFields = payload.fields;
      }
    }

    // Build target info
    const column = this.visibleColumns[targetCol];
    const target: PasteTarget | null = column ? { row: targetRow, col: targetCol, field: column.field, bounds } : null;

    // Fill-selection tiling only applies to a bounded (multi-cell) selection.
    const fillSelection = this.config.fillSelection === true && bounds !== null;

    // Build field list for paste width (constrained by bounds if set). When
    // filling the selection, extend fields across the full selection width so
    // the source can be tiled to fill every selected column; otherwise cap at
    // the clipboard's own width.
    const fields: string[] = [];
    const pasteWidth = parsed[0]?.length ?? 0;
    const fieldWidth = fillSelection ? maxCol - targetCol + 1 : pasteWidth;
    for (let i = 0; i < fieldWidth && targetCol + i <= maxCol; i++) {
      const col = this.visibleColumns[targetCol + i];
      if (col) {
        fields.push(col.field);
      }
    }

    const detail: PasteDetail = { rows: parsed, text, target, fields, fillSelection, rawRows, sourceFields };

    // Emit the event for any listeners
    this.emit<PasteDetail>('paste', detail);

    // Apply paste data using the configured handler (or default)
    this.#applyPasteHandler(detail);
  }

  /**
   * Apply the paste handler to update grid data.
   *
   * Uses the configured `pasteHandler`, or the default handler if not specified.
   * Set `pasteHandler: null` in config to disable auto-paste.
   */
  #applyPasteHandler(detail: PasteDetail): void {
    if (!this.grid) return;

    const { pasteHandler } = this.config;

    // pasteHandler: null means explicitly disabled
    if (pasteHandler === null) return;

    // Use custom handler or default
    const handler = pasteHandler ?? defaultPasteHandler;
    handler(detail, this.grid);
  }

  /**
   * Drop a header row that this grid itself copied, so a round-trip paste
   * never lands a header label in a data cell.
   *
   * A row copied with `includeHeaders` consists entirely of this grid's own
   * column labels (`header || field`). Detect that by matching every cell of the
   * first parsed row against the full set of column labels — independent of
   * which column the user pastes INTO. This is essential for **cross-column**
   * paste (e.g. copying "Source terminal" and pasting into "Terminal route"):
   * the copied header carries the SOURCE column's label, which does not equal
   * the target column's label but is still one of this grid's labels. Matching
   * the label set fixes cross-column paste while leaving external pastes (whose
   * first row is data, not labels) untouched.
   *
   * Guarded to avoid eating a legitimate first data row:
   * 1. The grid is configured with `includeHeaders` (it copies headers), and
   * 2. there is more than one row (never strip the only row), and
   * 3. every cell of the first row equals some column's label.
   */
  #dropCopiedHeaderRow(parsed: string[][]): void {
    if (!this.config.includeHeaders) return;
    // Never strip away the only row — that would make paste a no-op.
    if (parsed.length <= 1) return;

    const headerRow = parsed[0];
    if (headerRow.length === 0) return;

    const labels = this.#columnLabels();
    for (const cell of headerRow) {
      // A cell that isn't a known column label → this is real data, keep the row.
      if (!labels.has(cell)) return;
    }

    parsed.shift();
  }

  /**
   * Set of this grid's column labels (`header || field`), matching how
   * {@link buildClipboardText} serializes the copied header row. Used to detect
   * a header row this grid copied, regardless of paste position.
   */
  #columnLabels(): Set<string> {
    const labels = new Set<string>();
    for (const col of this.columns) {
      const label = col.header || col.field;
      if (label) labels.add(label);
    }
    return labels;
  }

  /**
   * True when the paste event originated from a focusable form control or
   * contenteditable element — i.e. somewhere the user expects the browser's
   * native paste behavior (typing into an editor input). Lets EditingPlugin
   * editors, filter inputs, and other text fields receive paste normally
   * instead of having ClipboardPlugin swallow the event.
   */
  #isEditablePasteTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    if (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) {
      return true;
    }
    return false;
  }

  /**
   * Get the current selection via Query System.
   * Returns undefined if no selection plugin is loaded or nothing is selected.
   */
  #getSelection(): SelectionQueryResult | undefined {
    const responses = this.grid?.query<SelectionQueryResult>('getSelection');
    return responses?.[0];
  }

  /**
   * Resolve columns and rows to include based on options and/or current selection.
   *
   * Priority for columns:
   *   1. `options.columns` (explicit field list)
   *   2. Selection union column bounds across ALL ranges (range/cell mode only)
   *   3. All visible non-utility columns
   *
   * Priority for rows:
   *   1. `options.rowIndices` (explicit indices)
   *   2. Selection union row bounds across ALL ranges
   *   3. All rows
   *
   * `contains(rowOffset, colOffset)` (when non-null) reports whether a cell of
   * the resolved grid lies inside at least one selected range — callers use it
   * to blank the gaps between disjoint ranges, matching Excel's rectangular
   * multi-range copy.
   */
  #resolveData(options?: CopyOptions): {
    columns: ColumnConfig[];
    rows: Record<string, unknown>[];
    contains: ((rowOffset: number, colOffset: number) => boolean) | null;
  } {
    const selection = this.#getSelection();
    const ranges = selection?.ranges ?? [];
    const isRowAxis = selectionAxis(selection) === 'row';

    // Union bounding box across ALL ranges. A multi-range selection (CTRL+drag
    // repeatedly) must copy every range, not just the last one drawn; min/max
    // also normalizes reversed (bottom-up / right-to-left) drags.
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;
    for (const r of ranges) {
      minRow = Math.min(minRow, r.from.row, r.to.row);
      maxRow = Math.max(maxRow, r.from.row, r.to.row);
      minCol = Math.min(minCol, r.from.col, r.to.col);
      maxCol = Math.max(maxCol, r.from.col, r.to.col);
    }

    // --- Columns ---
    let columns: ColumnConfig[];
    // Absolute visible-column index per resolved column (null when the columns
    // did not come from the selection). `resolveColumns` may drop utility /
    // hidden columns from the slice, so offsets can't be derived arithmetically.
    let colIndices: number[] | null = null;
    if (options?.columns) {
      // Caller specified exact fields
      columns = resolveColumns(this.columns, options.columns);
    } else if (ranges.length && !isRowAxis) {
      // Range/cell selection: restrict to selection column bounds
      // Selection indices are visible-column indices (from data-col)
      columns = resolveColumns(this.visibleColumns.slice(minCol, maxCol + 1));
      colIndices = columns.map((col) => this.visibleColumns.indexOf(col));
    } else {
      // Row selection or no selection: all visible columns
      columns = resolveColumns(this.columns);
    }

    // --- Rows ---
    let rows: Record<string, unknown>[];
    let rowIndices: number[] | null = null;
    if (options?.rowIndices) {
      // Caller specified exact row indices
      rows = resolveRows(this.rows as Record<string, unknown>[], options.rowIndices);
    } else if (ranges.length) {
      // Selection range: extract the union row span. Row mode selects WHOLE
      // rows and gets no column mask, so gaps between disjoint row ranges
      // (Ctrl+click rows 0, 5, 9) must be dropped outright rather than copied.
      const dropUnselectedRows = isRowAxis;
      const rowIsSelected = (r: number) =>
        ranges.some((g) => r >= Math.min(g.from.row, g.to.row) && r <= Math.max(g.from.row, g.to.row));
      rows = [];
      rowIndices = [];
      for (let r = minRow; r <= maxRow; r++) {
        const row = this.rows[r] as Record<string, unknown> | undefined;
        if (!row) continue;
        if (dropUnselectedRows && !rowIsSelected(r)) continue;
        rows.push(row);
        rowIndices.push(r);
      }
    } else {
      // No selection: all rows
      rows = this.rows as Record<string, unknown>[];
    }

    // Masking is only meaningful when BOTH axes came from the selection —
    // explicit `options.columns` / `options.rowIndices` (e.g. `copyRows()`)
    // would otherwise blank everything.
    const rowAbs = rowIndices;
    const colAbs = colIndices;
    const contains =
      rowAbs && colAbs
        ? (rowOffset: number, colOffset: number): boolean => {
            const row = rowAbs[rowOffset];
            const col = colAbs[colOffset];
            return ranges.some(
              (r) =>
                row >= Math.min(r.from.row, r.to.row) &&
                row <= Math.max(r.from.row, r.to.row) &&
                col >= Math.min(r.from.col, r.to.col) &&
                col <= Math.max(r.from.col, r.to.col),
            );
          }
        : null;

    return { columns, rows, contains };
  }

  /**
   * Build delimited text from resolved columns and rows.
   *
   * When no `processCell` callback is configured, uses "copy what you see" logic:
   * 1. Column `format` function (includes typeDefaults merged at config time)
   * 2. DOM cell textContent for columns with custom renderers (visible rows only)
   * 3. Raw value via `formatValueAsText` as final fallback
   */
  #buildText(
    columns: ColumnConfig[],
    rows: Record<string, unknown>[],
    options?: CopyOptions,
    contains?: ((rowOffset: number, colOffset: number) => boolean) | null,
  ): string {
    return this.#joinDisplayGrid(this.#buildDisplayGrid(columns, rows, options, contains), options);
  }

  /**
   * Build the 2D grid of DISPLAY strings for the given columns/rows (the same
   * WYSIWYG text used for `text/plain` and the `text/html` table). Header is
   * included only when configured.
   *
   * `contains` blanks cells that sit inside the selection's union bounding box
   * but outside every individual range, so a multi-range copy stays rectangular.
   */
  #buildDisplayGrid(
    columns: ColumnConfig[],
    rows: Record<string, unknown>[],
    options?: CopyOptions,
    contains?: ((rowOffset: number, colOffset: number) => boolean) | null,
  ): { header: string[] | null; rows: string[][] } {
    const includeHeaders = options?.includeHeaders ?? this.config.includeHeaders ?? false;
    const processCell = options?.processCell ?? this.config.processCell;
    const header = includeHeaders ? columns.map((c) => c.header || c.field) : null;
    const dataRows = rows.map((row, rowOffset) =>
      columns.map((col, colOffset) => {
        if (contains && !contains(rowOffset, colOffset)) return '';
        const value = resolveCellValue(row, col);
        if (processCell) return processCell(value, col.field, row);
        return this.#formatCellAsDisplayed(col, value, row);
      }),
    );
    return { header, rows: dataRows };
  }

  /** Join a display grid into delimited text (header row first when present). */
  #joinDisplayGrid(display: { header: string[] | null; rows: string[][] }, options?: CopyOptions): string {
    const delimiter = options?.delimiter ?? this.config.delimiter ?? '\t';
    const newline = options?.newline ?? this.config.newline ?? '\n';
    const lines: string[] = [];
    if (display.header) lines.push(display.header.join(delimiter));
    for (const cells of display.rows) lines.push(cells.join(delimiter));
    return lines.join(newline);
  }

  /**
   * Format a cell value the way the grid displays it.
   *
   * Priority:
   * 1. Column `format` function (includes typeDefaults applied at config merge time)
   * 2. DOM textContent for columns with a custom renderer (virtualized rows only)
   * 3. Raw value via `formatValueAsText`
   */
  #formatCellAsDisplayed(col: ColumnConfig, value: unknown, row: Record<string, unknown>): string {
    // 1. Column format function (covers col.format and typeDefaults)
    if (col.format) {
      try {
        const formatted = col.format(value, row);
        return formatted == null ? '' : String(formatted);
      } catch {
        // Format failed — fall through to next strategy
      }
    }

    // 2. DOM textContent for columns with custom renderers
    if (col.renderer || col.viewRenderer) {
      const text = this.#readCellTextFromDOM(col.field, row);
      if (text != null) return text;
    }

    // 3. Fallback to raw value formatting
    return formatValueAsText(value);
  }

  /**
   * Try to read a cell's displayed text from the DOM.
   *
   * Only works for rows currently rendered in the virtualized viewport.
   * Returns `null` if the row is not in the DOM (caller should fall back).
   */
  #readCellTextFromDOM(field: string, row: Record<string, unknown>): string | null {
    const host = this.gridElement;
    if (!host) return null;

    // Find the row index in the grid's current row array
    const rowIndex = this.rows.indexOf(row);
    if (rowIndex === -1) return null;

    // Query the rendered cell by row index and field name
    const cell = host.querySelector<HTMLElement>(`.cell[data-row="${rowIndex}"][data-field="${field}"]`);
    if (!cell) return null;

    return readCellDisplayText(cell);
  }

  /**
   * Get focused cell coordinates from DOM.
   * Used as fallback when SelectionPlugin has no selection.
   */
  #getFocusedCellFromDOM(target: HTMLElement): { row: number; col: number } | null {
    // Cells carry `data-field` + `data-row` (see `data-field-cache` was never a
    // real attribute). Body cells have `data-row`; header cells don't, so the
    // `data-row` guard below excludes headers.
    const cell = target.closest('.cell[data-field]') as HTMLElement | null;
    if (!cell) return null;

    const field = cell.dataset.field;
    const rowIndexStr = cell.dataset.row;
    if (!field || !rowIndexStr) return null;

    const row = parseInt(rowIndexStr, 10);
    if (isNaN(row)) return null;

    const col = this.columns.findIndex((c) => c.field === field);
    if (col === -1) return null;

    return { row, col };
  }
  // #endregion

  // #region Public API

  /**
   * Get the text representation of the current selection (or specified data)
   * without writing to the system clipboard.
   *
   * Useful for previewing what would be copied, or for feeding the text into
   * a custom UI (e.g., a "copy with column picker" dialog).
   *
   * @param options - Control which columns/rows to include
   * @returns Delimited text, or empty string if nothing to copy
   *
   * @example Get text for specific columns
   * ```ts
   * const clipboard = grid.getPluginByName('clipboard');
   * const text = clipboard.getSelectionAsText({
   *   columns: ['name', 'email'],
   *   includeHeaders: true,
   * });
   * console.log(text);
   * // "Name\tEmail\nAlice\talice@example.com\n..."
   * ```
   */
  getSelectionAsText(options?: CopyOptions): string {
    const { columns, rows, contains } = this.#resolveData(options);
    if (columns.length === 0 || rows.length === 0) return '';
    return this.#buildText(columns, rows, options, contains);
  }

  /**
   * Copy data to the system clipboard.
   *
   * Without options, copies the current selection (or entire grid if no selection).
   * With options, copies exactly the specified columns and/or rows — ideal for
   * "copy with column picker" workflows where the user selects rows first,
   * then chooses which columns to include via a dialog.
   *
   * @param options - Control which columns/rows to include
   * @returns The copied text
   *
   * @example Copy current selection (default)
   * ```ts
   * const clipboard = grid.getPluginByName('clipboard');
   * await clipboard.copy();
   * ```
   *
   * @example Copy specific columns from specific rows
   * ```ts
   * // User selected rows in the grid, then picked columns in a dialog
   * const selectedRowIndices = [0, 3, 7];
   * const chosenColumns = ['name', 'department', 'salary'];
   * await clipboard.copy({
   *   rowIndices: selectedRowIndices,
   *   columns: chosenColumns,
   *   includeHeaders: true,
   * });
   * ```
   */
  async copy(options?: CopyOptions): Promise<string> {
    const { columns, rows, contains } = this.#resolveData(options);
    if (columns.length === 0 || rows.length === 0) {
      return '';
    }

    const display = this.#buildDisplayGrid(columns, rows, options, contains);
    const text = this.#joinDisplayGrid(display, options);
    // Snapshot the RAW cell values (same column order as the text) for a
    // structured round-trip. Cloned so later row mutations don't change it.
    // Masked gaps between disjoint ranges stay blank here too, so a same-app
    // paste can't resurrect values the user never selected.
    const rawRows = rows.map((row, rowOffset) =>
      columns.map((col, colOffset) =>
        contains && !contains(rowOffset, colOffset) ? '' : cloneStructured(resolveCellValue(row, col)),
      ),
    );
    const fields = columns.map((col) => col.field);
    // Write BOTH text/plain (WYSIWYG for external targets) and text/html (a
    // table that also embeds the structured payload) so a same-app paste — even
    // cross-grid or cross-window — restores object cells losslessly.
    await copyToClipboard(text, buildClipboardHtml(display.header, display.rows, { v: 1, fields, rows: rawRows }));
    this.lastCopied = { text, timestamp: Date.now() };
    this.#internalClipboard = { text, rawRows, fields };
    this.emit<CopyDetail>('copy', {
      text,
      rowCount: rows.length,
      columnCount: columns.length,
    });
    return text;
  }

  /**
   * Copy specific rows by index to clipboard.
   *
   * Convenience wrapper around {@link copy} for row-based workflows.
   * Supports non-contiguous row indices (e.g., `[0, 3, 7]`).
   *
   * @param indices - Array of row indices to copy
   * @param options - Additional copy options (columns, headers, etc.)
   * @returns The copied text
   *
   * @example
   * ```ts
   * const clipboard = grid.getPluginByName('clipboard');
   * // Copy only rows 0 and 5, including just name and email columns
   * await clipboard.copyRows([0, 5], { columns: ['name', 'email'] });
   * ```
   */
  async copyRows(indices: number[], options?: Omit<CopyOptions, 'rowIndices'>): Promise<string> {
    if (indices.length === 0) return '';
    return this.copy({ ...options, rowIndices: indices });
  }

  /**
   * Read and parse clipboard content.
   * @returns Parsed 2D array of cell values, or null if clipboard is empty
   */
  async paste(): Promise<string[][] | null> {
    const text = await readFromClipboard();
    if (!text) return null;
    return parseClipboardText(text, this.config);
  }

  /**
   * Get the last copied text and timestamp.
   * @returns The last copied info or null
   */
  getLastCopied(): { text: string; timestamp: number } | null {
    return this.lastCopied;
  }
  // #endregion
}

// #region Internal Types

/**
 * Range representation for clipboard operations.
 */
interface CellRange {
  from: { row: number; col: number };
  to: { row: number; col: number };
}

/**
 * Selection result returned by the Query System.
 * Matches the SelectionResult type from SelectionPlugin.
 */
interface SelectionQueryResult {
  mode: string | string[];
  activeAxis?: string;
  ranges: CellRange[];
  anchor: { row: number; col: number } | null;
}

/**
 * Axis that owns the current selection. `mode` may be an array (e.g.
 * `['range', 'column']`), so prefer `activeAxis` and fall back to a string `mode`.
 */
function selectionAxis(sel: SelectionQueryResult | undefined): string | undefined {
  return sel?.activeAxis ?? (typeof sel?.mode === 'string' ? sel.mode : undefined);
}
// #endregion

// Re-export types
export type { ClipboardConfig, CopyDetail, CopyOptions, PasteDetail } from './types';
